// libraries/components/src/components/pnr-locator/features/use-manage-trips-auth-flow.ts

"use client";

import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { withManageTripsNextClientHeaders } from "@jb-manage-trips/api/manage-trips-client-headers";
import { fetchUpcomingTrips } from "../../shared/upcoming-trips";
import type { ReservationByTBResponse } from "@jb-manage-trips/types/itinerary";
import { AuthContext } from "../../../../../../apps/managetrips/src/components/auth-wrapper/auth-context";
import { useManageTripsConfig } from "@jb-manage-trips/runtime-config";
import {
  createManageTripsHttpError,
  getDurationMs,
  getErrorTelemetry,
  getOpenTokenTtlBucket,
  getTelemetryStart,
  getTripCountBucket,
  logManageTripsEvent,
  type ManageTripsTelemetryOpenTokenTtlBucket,
  type ManageTripsTelemetryOperation,
  type ManageTripsTelemetryResultRoute,
  type ManageTripsTelemetryTripCountBucket,
} from "@jb-manage-trips/fe-logger/manage-trips-events";
/* cookie helpers */
function getCookie(name: string): string | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }

  const regex = new RegExp(`(?:^|; )${name}=([^;]*)`);
  const match = regex.exec(document.cookie);

  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

type CookieWriteOptions = {
  path?: string;
};

type CookieDomainOptions = {
  domain: string;
  secure: boolean;
};

function getCookieDomainOptions(): CookieDomainOptions | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }

  const hostname = document.location.hostname;

  if (
    hostname === "localhost" ||
    /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)
  ) {
    return { domain: hostname, secure: false };
  }

  const parts = hostname.split(".");

  return {
    domain: parts.length >= 2 ? `.${parts.slice(-2).join(".")}` : hostname,
    secure: true,
  };
}

function getCookieAttributeString(options: CookieWriteOptions = {}): string {
  const path = options.path ?? "/";
  const domainOptions = getCookieDomainOptions();
  const attributes = [`path=${path}`];

  if (domainOptions) {
    attributes.push(`domain=${domainOptions.domain}`);

    if (domainOptions.secure) {
      attributes.push("secure");
    }
  }

  return attributes.join("; ");
}

function setCookie(
  name: string,
  value: string,
  options: CookieWriteOptions = {},
): void {
  if (typeof document === "undefined") {
    return;
  }

  document.cookie = `${name}=${encodeURIComponent(
    value,
  )}; ${getCookieAttributeString(options)}`;
}

function removeCookie(name: string): void {
  if (typeof document === "undefined") {
    return;
  }

  document.cookie = `${name}=; Max-Age=0; ${getCookieAttributeString()}`;
}

function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

type OpenTokenShape = {
  jbSSOGenerateTokenServiceRS: {
    sabreOpenToken: string;
    ttl: string;
  };
  jbSSOValidateTokenServiceRS: {
    sabreOpenToken: string;
    ttl: string;
  };
};

type AuthUserInfo = {
  accessToken: string;
  email: string;
};

function getHttpErrorCode(label: string, status: number): string {
  const normalizedLabel = label
    .replaceAll(/([a-z])([A-Z])/g, "$1_$2")
    .replaceAll(/[^a-zA-Z0-9]+/g, "_")
    .toUpperCase();

  return `${normalizedLabel}_HTTP_${String(status)}`;
}

async function assertOkJson<T>(response: Response, label: string): Promise<T> {
  const text = await response.text().catch(() => "");

  if (!response.ok) {
    throw createManageTripsHttpError({
      errorCode: getHttpErrorCode(label, response.status),
      message: `${label} failed: ${String(response.status)} ${text}`.trim(),
      status: response.status,
    });
  }

  const trimmed = text.trim();

  if (!trimmed) {
    return {
      jbSSOGenerateTokenServiceRS: {
        sabreOpenToken: "",
        ttl: "0",
      },
      jbSSOValidateTokenServiceRS: {
        sabreOpenToken: "",
        ttl: "0",
      },
    } as T;
  }

  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    return JSON.parse(trimmed) as T;
  }

  const getTag = (tag: string) => {
    const match = new RegExp(`<${tag}>([^<]*)</${tag}>`).exec(trimmed);
    return match?.[1] ?? "";
  };

  return {
    jbSSOGenerateTokenServiceRS: {
      sabreOpenToken: getTag("sabreOpenToken"),
      ttl: getTag("ttl") || "0",
    },
    jbSSOValidateTokenServiceRS: {
      sabreOpenToken: getTag("sabreOpenToken"),
      ttl: getTag("ttl") || "0",
    },
  } as T;
}

async function generateOpenToken(options: {
  mock: boolean;
  accessToken: string;
  email: string;
  ssoUrl: string;
  ssoApiKey: string;
}): Promise<OpenTokenShape> {
  const { mock, accessToken, email, ssoUrl, ssoApiKey } = options;

  if (mock) {
    const MOCK_SABRE_OPEN_TOKEN = "MOCK_SABRE_OPEN_TOKEN";

    return {
      jbSSOGenerateTokenServiceRS: {
        sabreOpenToken: MOCK_SABRE_OPEN_TOKEN,
        ttl: "3600",
      },
      jbSSOValidateTokenServiceRS: { sabreOpenToken: "", ttl: "" },
    };
  }

  const response = await fetch(`${ssoUrl}/generateOpenToken`, {
    body: JSON.stringify({
      jbSSOGenerateTokenServiceRQ: { accessToken, loginID: email },
    }),
    headers: withManageTripsNextClientHeaders({
      "Content-Type": "application/json",
      apikey: ssoApiKey,
    }),
    method: "POST",
  });

  return assertOkJson<OpenTokenShape>(response, "generateOpenToken");
}

export function useManageTripsAuthFlow() {
  const router = useRouter();
  const searchParameters = useSearchParams();
  const auth = useContext(AuthContext);
  const queryClient = useQueryClient();

  const config = useManageTripsConfig();

  const [isChecking, setIsChecking] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setError] = useState<string | undefined>();
  const [authUserInfo, setAuthUserInfo] = useState<AuthUserInfo | null>(null);

  const isSignedIn =
    Boolean(auth.IS_TOKEN_STORED) && Boolean(auth.PROFILE?.customerDetails);

  const accessToken = auth.ACCESS_TOKEN ?? "";
  const emailFromProfile =
    auth.PROFILE?.customerDetails.contact.emails[0] ?? "";

  const routerReference = useRef(router);
  routerReference.current = router;

  const searchParametersReference = useRef(searchParameters);
  searchParametersReference.current = searchParameters;

  const configReference = useRef(config);
  configReference.current = config;

  const authFlowReference = useRef<() => Promise<void>>(() =>
    Promise.resolve(),
  );
  // eslint-disable-next-line sonarjs/cognitive-complexity -- existing authenticated flow preserved; telemetry summarized around existing decision points
  authFlowReference.current = async () => {
    if (!isSignedIn) {
      setIsChecking(false);
      return;
    }
    const telemetryStart = getTelemetryStart();
    let failedOperation: ManageTripsTelemetryOperation = "validate_open_token";
    let hasOpenToken: boolean | undefined;
    let openTokenTtlBucket: ManageTripsTelemetryOpenTokenTtlBucket | undefined;
    let tripCountBucket: ManageTripsTelemetryTripCountBucket | undefined;
    let resultRoute: ManageTripsTelemetryResultRoute | undefined;
    setIsChecking(false);
    if (!accessToken) {
      logManageTripsEvent({
        durationMs: getDurationMs(telemetryStart),
        errorType: "MissingAccessToken",
        eventName: "authenticated_flow_failed",
        feature: "authenticated-trips",
        flow: "authenticated",
        operation: "validate_open_token",
        status: "failure",
      });
      setError("Unable to retrieve access token.");
      return;
    }

    const email = emailFromProfile;
    setAuthUserInfo({ accessToken, email });

    try {
      setIsLoading(true);

      const {
        MANAGE_TRIPS: {
          API: {
            RESERVATIONS_BY_TB_URL,
            SABRE_DX_URL,
            SABRE_OPEN_TOKEN_COOKIE_NAME,
            SABRE_OPEN_TOKEN_COOKIE_TIMEOUT,
            SABRE_RETURN_URL_COOKIE_NAME,
            SSO_URL,
            SSO_API_KEY,
          },
          ROUTES: { ITINERARY },
        },
      } = configReference.current;

      const cookieName = SABRE_OPEN_TOKEN_COOKIE_NAME;
      const timeoutSec = Number(SABRE_OPEN_TOKEN_COOKIE_TIMEOUT);
      const openToken = getCookie(cookieName);
      hasOpenToken = Boolean(openToken);
      const mock = process.env.NEXT_PUBLIC_USE_DEV_MOCKS === "true";
      /*
       * Middle-ground telemetry model:
       *
       * 1. One start event per authenticated attempt.
       * 2. One completed/failed summary event after final outcome is known.
       *
       * The start event is intentionally fire-and-forget because it should not
       * delay the user interaction. Story #1 guarantees logManageTripsEvent()
       * will not throw into application code.
       */
      logManageTripsEvent({
        eventName: "authenticated_flow_started",
        feature: "authenticated-trips",
        flow: "authenticated",
        hasOpenToken,
        operation: "validate_open_token",
        status: "started",
      });
      const generateAndStoreOpenToken = async (): Promise<OpenTokenShape> => {
        failedOperation = "generate_open_token";
        const generated = await generateOpenToken({
          mock,
          accessToken,
          email,
          ssoUrl: SSO_URL,
          ssoApiKey: SSO_API_KEY,
        });
        setCookie(
          cookieName,
          generated.jbSSOGenerateTokenServiceRS.sabreOpenToken,
        );

        return generated;
      };
      if (openToken) {
        failedOperation = "validate_open_token";
        let validated: OpenTokenShape;
        if (mock) {
          validated = {
            jbSSOGenerateTokenServiceRS: { sabreOpenToken: "", ttl: "" },
            jbSSOValidateTokenServiceRS: {
              sabreOpenToken: openToken,
              ttl: "3600",
            },
          };
        } else {
          const response = await fetch(`${SSO_URL}/validateOpenToken`, {
            body: JSON.stringify({
              jbSSOValidateTokenServiceRQ: {
                accessToken,
                sabreOpenToken: openToken,
              },
            }),
            headers: withManageTripsNextClientHeaders({
              "Content-Type": "application/json",
              apikey: SSO_API_KEY,
            }),
            method: "POST",
          });

          if (response.status === 400) {
            const text = await response.text().catch(() => "");

            console.warn(
              "[useManageTripsAuthFlow] validateOpenToken expired:",
              text,
            );
            validated = {
              jbSSOGenerateTokenServiceRS: { sabreOpenToken: "", ttl: "" },
              jbSSOValidateTokenServiceRS: {
                sabreOpenToken: openToken,
                ttl: "0",
              },
            };
          } else {
            validated = await assertOkJson<OpenTokenShape>(
              response,
              "validateOpenToken",
            );

            if (!validated.jbSSOValidateTokenServiceRS.sabreOpenToken) {
              validated.jbSSOValidateTokenServiceRS.sabreOpenToken = openToken;
            }
          }
        }
        const validatedTtl = Number(validated.jbSSOValidateTokenServiceRS.ttl);
        openTokenTtlBucket = getOpenTokenTtlBucket(validatedTtl);
        if (validatedTtl < timeoutSec) {
          await generateAndStoreOpenToken();
        }
      } else {
        openTokenTtlBucket = "missing";
        await generateAndStoreOpenToken();
      }

      const returnValueName = SABRE_RETURN_URL_COOKIE_NAME;
      const returnValueUrl = getCookie(returnValueName);

      if (returnValueUrl) {
        removeCookie(returnValueName);
        resultRoute = "sabre-dx";
        logManageTripsEvent({
          durationMs: getDurationMs(telemetryStart),
          eventName: "authenticated_flow_completed",
          feature: "authenticated-trips",
          flow: "authenticated",
          hasOpenToken,
          openTokenTtlBucket,
          operation: "route_decision",
          resultRoute,
          status: "success",
        });
        if (isValidUrl(returnValueUrl)) {
          globalThis.location.href = returnValueUrl;
          return;
        }

        const derived = new URL(SABRE_DX_URL);
        const locale = searchParametersReference.current.get("locale") ?? "en";

        derived.searchParams.set("locale", locale);
        globalThis.location.href = derived.toString();
        return;
      }
      failedOperation = "upcoming_trips_fetch";
      const tb: ReservationByTBResponse = await fetchUpcomingTrips({
        url: RESERVATIONS_BY_TB_URL,
        accessToken,
      });
      tripCountBucket = getTripCountBucket(tb.trips.length);
      queryClient.setQueryData(["upcoming-trips-itinerary"], tb);

      if (tb.trips.length > 0) {
        resultRoute = "itinerary";
        logManageTripsEvent({
          durationMs: getDurationMs(telemetryStart),
          eventName: "authenticated_flow_completed",
          feature: "authenticated-trips",
          flow: "authenticated",
          hasOpenToken,
          openTokenTtlBucket,
          operation: "route_decision",
          resultRoute,
          status: "success",
          tripCountBucket,
        });
        routerReference.current.push(ITINERARY);
      } else {
        resultRoute = "none";
        logManageTripsEvent({
          durationMs: getDurationMs(telemetryStart),
          eventName: "authenticated_flow_completed",
          feature: "authenticated-trips",
          flow: "authenticated",
          hasOpenToken,
          openTokenTtlBucket,
          operation: "route_decision",
          resultRoute,
          status: "success",
          tripCountBucket: "0",
        });
        setError("We couldn’t find any trips for your profile.");
      }
    } catch (error) {
      logManageTripsEvent({
        ...getErrorTelemetry(error),
        durationMs: getDurationMs(telemetryStart),
        eventName: "authenticated_flow_failed",
        feature: "authenticated-trips",
        flow: "authenticated",
        hasOpenToken,
        openTokenTtlBucket,
        operation: failedOperation,
        resultRoute,
        status: "failure",
        tripCountBucket,
      });
      console.error("[useManageTripsAuthFlow] failed", error);
      setError("A service error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void authFlowReference.current();
  }, [isSignedIn]);

  return useMemo(
    () => ({
      authUserInfo,
      errorMessage,
      isChecking,
      isLoading,
      isSignedIn,
      setError,
    }),
    [authUserInfo, errorMessage, isChecking, isLoading, isSignedIn],
  );
}
